import React, { useState, useEffect } from "react";
import { MapContainer, Marker, useMapEvents } from "react-leaflet";
import ReactLeafletGoogleLayer from "react-leaflet-google-layer";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
  Box,
} from "@mui/material";
import RoomIcon from "@mui/icons-material/Room";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const getDefaultCenter = () => {
  const storedLat = parseFloat(localStorage.getItem("mapCenterLat"));
  const storedLng = parseFloat(localStorage.getItem("mapCenterLng"));
  if (!isNaN(storedLat) && !isNaN(storedLng)) {
    return [storedLat, storedLng];
  }
  return [41.86178, -87.90215];
};

const getDefaultZoom = () => {
  const z = parseInt(localStorage.getItem("mapZoom"), 10);
  return isNaN(z) ? 10 : z;
};

const ClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
};

const MapClicker = ({
  open,
  onClose,
  onLocationSelected,
  latitude,
  longitude,
  title,
}) => {
  const parsedLat = parseFloat(latitude);
  const parsedLng = parseFloat(longitude);
  const hasInitialPos =
    !isNaN(parsedLat) &&
    !isNaN(parsedLng) &&
    parsedLat !== 0 &&
    parsedLng !== 0;

  const [pendingLat, setPendingLat] = useState(
    hasInitialPos ? parsedLat : null,
  );
  const [pendingLng, setPendingLng] = useState(
    hasInitialPos ? parsedLng : null,
  );

  useEffect(() => {
    if (open) {
      const lat = parseFloat(latitude);
      const lng = parseFloat(longitude);
      setPendingLat(!isNaN(lat) && lat !== 0 ? lat : null);
      setPendingLng(!isNaN(lng) && lng !== 0 ? lng : null);
    }
  }, [open, latitude, longitude]);

  const handleMapClick = (lat, lng) => {
    setPendingLat(lat);
    setPendingLng(lng);
  };

  const handleConfirm = () => {
    if (pendingLat !== null && pendingLng !== null) {
      onLocationSelected(
        Math.round(pendingLat * 1e6) / 1e6,
        Math.round(pendingLng * 1e6) / 1e6,
      );
    }
  };

  const handleClear = () => {
    setPendingLat(null);
    setPendingLng(null);
    onLocationSelected(0, 0);
  };

  const center =
    pendingLat !== null && pendingLng !== null
      ? [pendingLat, pendingLng]
      : getDefaultCenter();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogContent sx={{ p: 0 }}>
        <Typography variant="body2" sx={{ px: 2, pt: 1, pb: 0.5 }}>
          Click anywhere on the map to set the location.
        </Typography>
        {pendingLat !== null && pendingLng !== null && (
          <Box
            sx={{
              px: 2,
              pb: 0.5,
              display: "flex",
              alignItems: "center",
              gap: 0.5,
            }}
          >
            <RoomIcon fontSize="small" color="error" />
            <Typography variant="body2">
              {pendingLat.toFixed(6)}, {pendingLng.toFixed(6)}
            </Typography>
          </Box>
        )}
        <Box sx={{ height: 420, width: "100%" }}>
          {open && (
            <MapContainer
              center={center}
              zoom={getDefaultZoom()}
              style={{ height: "100%", width: "100%" }}
              zoomControl={true}
            >
              <ReactLeafletGoogleLayer
                apiKey={process.env.REACT_APP_GOOGLE_KEY}
                type="roadmap"
              />
              <ClickHandler onMapClick={handleMapClick} />
              {pendingLat !== null && pendingLng !== null && (
                <Marker position={[pendingLat, pendingLng]} />
              )}
            </MapContainer>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button size="small" onClick={handleClear}>
          Clear Location
        </Button>
        <Button size="small" onClick={onClose}>
          Cancel
        </Button>
        <Button
          size="small"
          variant="contained"
          onClick={handleConfirm}
          disabled={pendingLat === null || pendingLng === null}
        >
          Use This Location
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MapClicker;
